/**
 * @fileoverview Landing page section showcasing cost breakdown visualization.
 * Two-column layout with donut chart and feature description.
 * @module features/landing/components/CostBreakdownSection
 */

import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { LandingCostDonut } from './LandingCostDonut'
import { TypingAnimation } from '@/components/ui/typing-animation'

/**
 * Landing page section with cost breakdown donut chart and description.
 * Explains the value proposition of automatic cost tracking.
 *
 * @component
 */
export function CostBreakdownSection(): ReactElement {
  const { t } = useTranslation('features-landing')
  const typingPhrases = t('costBreakdown.typingPhrases', { returnObjects: true }) as string[]

  return (
    <section id="features" className="py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left column: Donut chart */}
          <div className="flex flex-col items-center lg:items-start">
            <LandingCostDonut className="w-full" />
            <p className="text-xs text-foreground/70 text-center mt-2 w-full">{t('costBreakdown.hoverHint')}</p>
          </div>

          {/* Right column: Description */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold tracking-tight text-foreground">
              <TypingAnimation
                words={typingPhrases}
                loop
                typeSpeed={80}
                deleteSpeed={40}
                pauseDelay={2500}
                cursorStyle="line"
                startOnView={false}
              />
              <span className="block text-xl md:text-2xl lg:text-3xl font-light text-muted-foreground -mt-3">
                {t('costBreakdown.atAGlance')}
              </span>
            </h2>

          </div>
        </div>
      </div>
    </section>
  )
}
